import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { RefreshCw, Clock, EyeOff, Eye, GitMerge, Pencil, Trash2, ExternalLink, X } from "lucide-react";

const LABEL = { teams: "team", events: "event", "trainer-listings": "listing" };
const PUB = { teams: "/teams", events: "/events", "trainer-listings": "/trainer-listings" };

export default function AdminContentRow({ kind, item, onChange }) {
    const [dlg, setDlg] = useState(null); // owner | merge | history | null

    const remove = async () => {
        const undoable = kind === "teams";
        const msg = undoable
            ? `Delete "${item.name}"? Its rosters, reviews, followers and posts go too — you'll get a few seconds to undo.`
            : `Delete "${item.name}"? This can't be undone.`;
        if (!window.confirm(msg)) return;
        try {
            // Teams: delete the whole program (all rosters); other kinds: single listing.
            const url = kind === "teams" ? `/teams/${item.id}/program` : `/admin/${kind}/${item.id}`;
            const { data } = await api.delete(url);
            onChange();
            if (data?.bin_id) {
                toast.success(`Deleted "${item.name}"`, {
                    duration: 9000,
                    action: {
                        label: "Undo",
                        onClick: async () => {
                            try { await api.post(`/teams/restore/${data.bin_id}`); toast.success("Restored"); onChange(); }
                            catch (e) { toast.error(e?.response?.data?.detail || "Undo failed"); }
                        },
                    },
                });
            } else {
                toast.success("Deleted");
            }
        } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };
    const toggleHide = async () => {
        try { await api.post(`/admin/${kind}/${item.id}/hide`, { hidden: !item.hidden }); toast.success(item.hidden ? "Restored to site" : "Hidden from site"); onChange(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    const created = item.created_at ? new Date(item.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "";
    const since = item.owner_since ? new Date(item.owner_since).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : created;

    return (
        <div className="border border-[#2a2a2a] bg-[#141414] p-4" data-testid={`admin-${kind}-${item.id}`}>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
                <div className="flex items-center gap-3 min-w-0">
                    {item.logo && <img src={item.logo} alt="" className="h-10 w-10 border border-[#2a2a2a] object-contain bg-[#0a0a0a]" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
                    <div className="min-w-0">
                        <a href={`${PUB[kind]}/${item.id}`} target="_blank" rel="noreferrer" data-testid={`admin-${kind}-name-${item.id}`} className="font-display text-lg tracking-wide text-white hover:text-[#ff4500] inline-flex items-center gap-1">
                            {item.name} <ExternalLink className="h-3.5 w-3.5 opacity-60" />
                        </a>
                        <div className="text-[11px] text-[#a0a0a0]">
                            {[item.city, item.state].filter(Boolean).join(", ") || item.location || ""}
                            {item.circuit ? ` · ${item.circuit}` : ""}{item.age_group ? ` · ${item.age_group}` : ""}
                            {" · "}
                            {item.is_seed
                                ? <span data-testid={`source-seed-${item.id}`} className="text-[#8a8a8a] uppercase tracking-wider">seed</span>
                                : <span data-testid={`source-user-${item.id}`} className="text-[#4aa3f0] uppercase tracking-wider">user-added</span>}
                            {item.hidden ? <span className="text-[#ef4444] uppercase tracking-wider"> · hidden</span> : null}
                        </div>
                    </div>
                </div>

                <div className="border border-[#2a2a2a] bg-[#0f0f0f] px-3 py-1.5">
                    <div className="text-[9px] uppercase tracking-[0.18em] text-[#666]">Created by</div>
                    <div className="text-xs text-[#e5e5e5] mt-0.5">{item.created_by_username ? <span className="text-[#4aa3f0]">{item.created_by_username}</span> : "—"}{created ? ` · ${created}` : ""}</div>
                </div>
                <div className="border border-[#2a2a2a] bg-[#0f0f0f] px-3 py-1.5">
                    <div className="text-[9px] uppercase tracking-[0.18em] text-[#666]">Controlled by</div>
                    <div className="text-xs text-[#e5e5e5] mt-0.5">{item.owner_username ? <><span className="text-[#4aa3f0]">{item.owner_username}</span> · since {since}</> : <span className="text-[#f59e0b]">Unclaimed</span>}</div>
                </div>

                <div className="flex gap-2 flex-wrap ml-auto">
                    <IconBtn testid={`owner-${item.id}`} onClick={() => setDlg("owner")} icon={RefreshCw} label="Update owner" />
                    <IconBtn testid={`history-${item.id}`} onClick={() => setDlg("history")} icon={Clock} label="History" />
                    <IconBtn testid={`hide-${item.id}`} onClick={toggleHide} icon={item.hidden ? Eye : EyeOff} label={item.hidden ? "Unhide" : "Hide"} />
                    <IconBtn testid={`merge-${item.id}`} onClick={() => setDlg("merge")} icon={GitMerge} label="Merge" />
                    <a href={`${PUB[kind]}/${item.id}`} target="_blank" rel="noreferrer" data-testid={`admin-edit-${kind}-${item.id}`} title={`Edit ${LABEL[kind]} info`} className="inline-flex items-center justify-center rounded-none bg-[#ff4500] hover:bg-[#e03e00] h-9 w-9 text-white"><Pencil className="h-3.5 w-3.5" /></a>
                    <Button data-testid={`admin-delete-${kind}-${item.id}`} onClick={remove} className="rounded-none bg-transparent border border-[#2a2a2a] hover:border-red-500 hover:text-red-500 uppercase tracking-wider text-[10px] h-9"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
            </div>

            {dlg === "owner" && <OwnerDialog kind={kind} item={item} onClose={() => setDlg(null)} onDone={() => { setDlg(null); onChange(); }} />}
            {dlg === "merge" && <MergeDialog kind={kind} item={item} onClose={() => setDlg(null)} onDone={() => { setDlg(null); onChange(); }} />}
            {dlg === "history" && <HistoryDialog kind={kind} item={item} onClose={() => setDlg(null)} />}
        </div>
    );
}

function IconBtn({ onClick, icon: Icon, label, testid }) {
    return (
        <Button data-testid={testid} onClick={onClick} className="rounded-none bg-transparent border border-[#2a2a2a] hover:border-[#ff4500] uppercase tracking-wider text-[10px] h-9">
            <Icon className="h-3.5 w-3.5 mr-1" /> {label}
        </Button>
    );
}

function Shell({ title, onClose, children, testid }) {
    return (
        <div className="fixed inset-0 z-[200] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid={testid}>
            <div className="bg-[#141414] border border-[#2a2a2a] w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <div className="font-display text-xl">{title}</div>
                    <button onClick={onClose} data-testid={`${testid}-close`}><X className="h-5 w-5 text-[#a0a0a0] hover:text-white" /></button>
                </div>
                {children}
            </div>
        </div>
    );
}

function OwnerDialog({ kind, item, onClose, onDone }) {
    const [ident, setIdent] = useState("");
    const [emailPrev, setEmailPrev] = useState(true);
    const [emailNew, setEmailNew] = useState(true);
    const [busy, setBusy] = useState(false);

    const save = async (clear) => {
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/${kind}/${item.id}/owner`, { identifier: clear ? "" : ident, email_previous: emailPrev, email_new: emailNew });
            toast.success(data.claimed ? `Owner set to @${data.owner_username}` : "Listing is now unclaimed");
            onDone();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
        finally { setBusy(false); }
    };

    return (
        <Shell title={`Update owner — ${item.name}`} onClose={onClose} testid="owner-dialog">
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#a0a0a0]">New controller (username or email)</label>
            <input data-testid="owner-identifier" value={ident} onChange={(e) => setIdent(e.target.value)} placeholder="e.g. coach.dominguez" className="mt-2 w-full h-10 bg-[#0f0f0f] border border-[#2a2a2a] px-3 text-sm text-white outline-none focus:border-[#ff4500]" />
            <p className="text-[11px] text-[#a0a0a0] mt-1.5">Leave blank &amp; click <b>Clear owner</b> to make it unclaimed.</p>
            <div className="mt-4 text-[10px] uppercase tracking-[0.18em] text-[#a0a0a0]">On save, email:</div>
            <label className="flex items-center gap-2 mt-2 text-sm cursor-pointer" data-testid="owner-email-prev">
                <input type="checkbox" checked={emailPrev} onChange={(e) => setEmailPrev(e.target.checked)} className="accent-[#ff4500]" />
                Previous claimant {item.owner_username ? <span className="text-[#4aa3f0]">{item.owner_username}</span> : <span className="text-[#666]">(none)</span>}
            </label>
            <label className="flex items-center gap-2 mt-2 text-sm cursor-pointer" data-testid="owner-email-new">
                <input type="checkbox" checked={emailNew} onChange={(e) => setEmailNew(e.target.checked)} className="accent-[#ff4500]" />
                New claimant
            </label>
            <div className="flex gap-2 mt-5">
                <Button data-testid="owner-save" disabled={busy || !ident.trim()} onClick={() => save(false)} className="rounded-none bg-[#ff4500] hover:bg-[#e03e00] text-xs uppercase tracking-wider h-10">Save changes</Button>
                <Button data-testid="owner-clear" disabled={busy} onClick={() => save(true)} className="rounded-none bg-[#141414] border border-[#2a2a2a] hover:border-white text-xs uppercase tracking-wider h-10">Clear owner</Button>
                <Button onClick={onClose} className="rounded-none bg-transparent border border-[#2a2a2a] text-xs uppercase tracking-wider h-10">Cancel</Button>
            </div>
        </Shell>
    );
}

function MergeDialog({ kind, item, onClose, onDone }) {
    const [q, setQ] = useState("");
    const [opts, setOpts] = useState([]);
    const [pick, setPick] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => {
            api.get(`/admin/${kind}/search?q=${encodeURIComponent(q)}&exclude=${item.id}`).then((r) => setOpts(r.data.items || [])).catch(() => setOpts([]));
        }, 250);
        return () => clearTimeout(t);
    }, [q, kind, item.id]);

    const doMerge = async () => {
        if (!pick) return;
        setBusy(true);
        try { const { data } = await api.post(`/admin/${kind}/${item.id}/merge`, { duplicate_id: pick.id }); toast.success(`Merged — moved ${data.moved.posts} posts, ${data.moved.follows} follows`); onDone(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Merge failed"); }
        finally { setBusy(false); }
    };

    return (
        <Shell title={`Merge into — ${item.name}`} onClose={onClose} testid="merge-dialog">
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#a0a0a0]">Duplicate to merge (search name)</label>
            <input data-testid="merge-search" value={q} onChange={(e) => { setQ(e.target.value); setPick(null); }} placeholder="Search…" className="mt-2 w-full h-10 bg-[#0f0f0f] border border-[#2a2a2a] px-3 text-sm text-white outline-none focus:border-[#ff4500]" />
            <div className="mt-2 max-h-40 overflow-y-auto border border-[#2a2a2a]">
                {opts.map((o) => (
                    <button key={o.id} data-testid={`merge-opt-${o.id}`} onClick={() => setPick(o)} className={`block w-full text-left px-3 py-2 text-sm ${pick?.id === o.id ? "bg-[#ff4500]/20 text-white" : "hover:bg-[#1c1c1c] text-[#cfcfcf]"}`}>
                        {o.name} <span className="text-[10px] text-[#666]">{[o.city, o.state].filter(Boolean).join(", ")}</span>
                    </button>
                ))}
                {opts.length === 0 && <div className="px-3 py-3 text-xs text-[#666]">No matches</div>}
            </div>
            {pick && (
                <div className="mt-4 border border-red-500/50 bg-red-500/10 p-3">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-[#ff6a4d] mb-1">⚠ This can't be undone</div>
                    <div className="text-xs text-[#e5e5e5]"><b>{item.name}</b> will remain. <b>"{pick.name}"</b> will be permanently removed, and its followers, posts and reviews move into <b>{item.name}</b>.</div>
                </div>
            )}
            <div className="flex gap-2 mt-5">
                <Button data-testid="merge-confirm" disabled={busy || !pick} onClick={doMerge} className="rounded-none bg-red-600 hover:bg-red-500 text-xs uppercase tracking-wider h-10">Merge &amp; remove duplicate</Button>
                <Button onClick={onClose} className="rounded-none bg-transparent border border-[#2a2a2a] text-xs uppercase tracking-wider h-10">Cancel</Button>
            </div>
        </Shell>
    );
}

function HistoryDialog({ kind, item, onClose }) {
    const [rows, setRows] = useState(null);
    useEffect(() => { api.get(`/admin/${kind}/${item.id}/history`).then((r) => setRows(r.data.items || [])).catch(() => setRows([])); }, [kind, item.id]);
    return (
        <Shell title={`History — ${item.name}`} onClose={onClose} testid="history-dialog">
            {rows == null ? <div className="text-sm text-[#666]">Loading…</div> : rows.length === 0 ? <div className="text-sm text-[#666]">No history yet.</div> : (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                    {rows.map((r) => (
                        <div key={r.id} className="border border-[#2a2a2a] bg-[#0f0f0f] p-2.5">
                            <div className="text-xs text-white">{r.detail}</div>
                            <div className="text-[10px] text-[#666] mt-0.5">{r.actor ? `by ${r.actor} · ` : ""}{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</div>
                        </div>
                    ))}
                </div>
            )}
        </Shell>
    );
}
